import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import TaskCategorizationPanel from '@/components/gamification/TaskCategorizationPanel';
import { CheckSquare, ChevronRight, ArrowLeft } from 'lucide-react';
import { format, startOfDay } from 'date-fns';

export default function RockTaskCreator({ open, onClose, issueId, officers }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1); // 1 = select officer+date, 2 = task form
  const [selectedOfficer, setSelectedOfficer] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [missionFilter, setMissionFilter] = useState('');
  const [objectiveFilter, setObjectiveFilter] = useState('');
  const [taskForm, setTaskForm] = useState({
    activity_id: '',
    task_description: '',
    key_result_focus: '',
    time_allocation_percent: 40,
    task_order: 1,
    status_flag: 'gray',
    category: null,
    xp_value: 0,
    ep_value: 0,
    skill_rewards: [],
    bot_suggested_category: null,
    bot_suggested_xp: null,
    bot_suggested_ep: null,
    bot_suggested_skills: null,
    bot_suggestion_approved: false,
  });

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: allActivities = [] } = useQuery({
    queryKey: ['allActivities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: dailyTasks = [] } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: objectives = [] } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: missions = [] } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  // Calculate next task order for the selected officer+date
  useEffect(() => {
    if (selectedOfficer && selectedDate) {
      const existingTasks = dailyTasks.filter(t => {
        if (t.user_email !== selectedOfficer) return false;
        const taskDate = new Date(t.date + 'T00:00:00');
        return format(taskDate, 'yyyy-MM-dd') === selectedDate;
      });
      const nextOrder = existingTasks.length + 1;
      const defaultAlloc = nextOrder === 1 ? 40 : nextOrder === 2 ? 30 : 20;
      setTaskForm(prev => ({
        ...prev,
        task_order: nextOrder,
        time_allocation_percent: defaultAlloc
      }));
    }
  }, [selectedOfficer, selectedDate, dailyTasks]);

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.DailyTask.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      handleClose();
    }
  });

  const handleClose = () => {
    setStep(1);
    setSelectedOfficer('');
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
    setMissionFilter('');
    setObjectiveFilter('');
    setTaskForm({
      activity_id: '',
      task_description: '',
      key_result_focus: '',
      time_allocation_percent: 40,
      task_order: 1,
      status_flag: 'gray',
      category: null, xp_value: 0, ep_value: 0, skill_rewards: [],
      bot_suggested_category: null, bot_suggested_xp: null, bot_suggested_ep: null,
      bot_suggested_skills: null, bot_suggestion_approved: false,
    });
    onClose();
  };

  const handleSubmit = () => {
    if (!taskForm.task_description || !taskForm.activity_id) {
      alert('Task description and activity are required');
      return;
    }

    const isCreatingForSelf = selectedOfficer === user?.email;
    const targetOfficer = officers.find(o => o.user_email === selectedOfficer);
    const isBot = targetOfficer?.is_bot || targetOfficer?.custom_role === 'AI Officer';

    const taskData = {
      date: selectedDate,
      user_email: selectedOfficer,
      activity_id: taskForm.activity_id,
      task_description: taskForm.task_description,
      key_result_focus: taskForm.key_result_focus,
      time_allocation_percent: isBot ? 0 : taskForm.time_allocation_percent,
      task_order: taskForm.task_order,
      status_flag: 'gray',
      status: (isCreatingForSelf || isBot) ? 'approved' : 'tentative',
      generated_by_ai: false,
      tags: [],
      category: taskForm.category,
      xp_value: taskForm.xp_value || 0,
      ep_value: taskForm.ep_value || 0,
      skill_rewards: taskForm.skill_rewards || [],
      bot_suggested_category: taskForm.bot_suggested_category,
      bot_suggested_xp: taskForm.bot_suggested_xp,
      bot_suggested_ep: taskForm.bot_suggested_ep,
      bot_suggested_skills: taskForm.bot_suggested_skills,
      bot_suggestion_approved: taskForm.bot_suggestion_approved || false,
    };

    if (!isCreatingForSelf && !isBot) {
      taskData.created_by_email = user?.email;
    }

    createTaskMutation.mutate(taskData);
  };

  const officerOptions = (officers || [])
    .filter(o => o.user_email && !o.is_bot)
    .map(o => ({
      value: o.user_email,
      label: `${o.callsign} (${o.rank_title})`
    }));

  const selectedActivity = allActivities.find(a => a.id === taskForm.activity_id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent data-testid="issues-rocktaskcreator-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-amber-400" />
            Create Task {step === 1 ? '— Select Officer & Date' : '— Task Details'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Officer</Label>
              <SearchableTagSelect
                options={officerOptions}
                value={selectedOfficer}
                onValueChange={setSelectedOfficer}
                placeholder="Select officer..."
                emptyMessage="No officers found."
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input data-testid="issues-rocktaskcreator-input"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-white border-slate-300 text-slate-900"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={handleClose} className="text-slate-600 border-slate-700 hover:bg-slate-600" data-testid="issues-cancel-btn">Cancel</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedOfficer || !selectedDate}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 text-sm text-slate-300">
              <span className="text-slate-500">Officer:</span> {officers.find(o => o.user_email === selectedOfficer)?.callsign} · 
              <span className="text-slate-500 ml-2">Date:</span> {format(new Date(selectedDate + 'T00:00:00'), 'MMM d, yyyy')}
            </div>

            <div className="space-y-2">
              <Label>Task Order & Time</Label>
              <div className="flex items-center gap-2">
                <Select value={taskForm.task_order.toString()} onValueChange={(v) => setTaskForm(prev => ({ ...prev, task_order: parseInt(v) }))}>
                  <SelectTrigger className="w-32 bg-slate-950 border-slate-700 text-slate-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <SelectItem key={n} value={n.toString()}>Task #{n}{n > 3 ? ' (Add.)' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input data-testid="issues-rocktaskcreator-input-1"
                  type="number"
                  value={taskForm.time_allocation_percent}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, time_allocation_percent: parseInt(e.target.value) || 0 }))}
                  className="w-20 bg-white border-slate-300 text-slate-900"
                />
                <span className="text-xs text-slate-400">% of day</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Filter by Mission</Label>
              <SearchableTagSelect
                options={missions.filter(m => m.id).map(m => ({
                  value: m.id,
                  label: `${m.owner_email === user?.email || m.sponsor_email === user?.email ? '⭐ ' : ''}${m.mission_code ? `[${m.mission_code}] ` : ''}${m.title}`
                }))}
                value={missionFilter}
                onValueChange={(v) => { setMissionFilter(v); setObjectiveFilter(''); setTaskForm(prev => ({ ...prev, activity_id: '' })); }}
                placeholder="All missions"
                emptyMessage="No missions found."
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>

            <div className="space-y-2">
              <Label>Filter by Objective</Label>
              <SearchableTagSelect
                options={objectives
                  .filter(o => o.id && (!missionFilter || o.mission_id === missionFilter))
                  .map(o => ({
                    value: o.id,
                    label: `${o.owner_email === user?.email ? '⭐ ' : ''}${o.objective_code ? `[${o.objective_code}] ` : ''}${o.statement}`
                  }))}
                value={objectiveFilter}
                onValueChange={(v) => { setObjectiveFilter(v); setTaskForm(prev => ({ ...prev, activity_id: '' })); }}
                placeholder={missionFilter ? 'All objectives' : 'Select mission first or show all'}
                emptyMessage="No objectives found."
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>

            <div className="space-y-2">
              <Label>Link to Activity *</Label>
              <SearchableTagSelect
                options={allActivities
                  .filter(a => {
                    if (a.status !== 'active' || !a.id) return false;
                    if (objectiveFilter && a.objective_id !== objectiveFilter) return false;
                    if (missionFilter && !objectiveFilter) {
                      const obj = objectives.find(o => o.id === a.objective_id);
                      if (!obj || obj.mission_id !== missionFilter) return false;
                    }
                    return true;
                  })
                  .map(a => ({
                    value: a.id,
                    label: `${a.accountable_email === user?.email || a.responsible_email === user?.email ? '⭐ ' : ''}[${a.activity_code}] ${a.title}`
                  }))}
                value={taskForm.activity_id}
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, activity_id: v }))}
                placeholder="Select activity..."
                emptyMessage="No activities found."
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>

            {selectedActivity?.key_results?.length > 0 && (
              <div className="space-y-2">
                <Label>Key Result Focus</Label>
                <Select value={taskForm.key_result_focus} onValueChange={(v) => setTaskForm(prev => ({ ...prev, key_result_focus: v }))}>
                  <SelectTrigger className="bg-slate-950 border-slate-700"><SelectValue placeholder="Select key result..." /></SelectTrigger>
                  <SelectContent>
                    {selectedActivity.key_results.filter(kr => kr.description).map((kr, idx) => (
                      <SelectItem key={idx} value={kr.description}>{kr.description} ({kr.current_value}/{kr.target} {kr.unit})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Task Description *</Label>
              <Textarea data-testid="issues-rocktaskcreator-textarea"
                value={taskForm.task_description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, task_description: e.target.value }))}
                placeholder="What needs to be accomplished?"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px]"
              />
            </div>

            {/* Gamification Points */}
            <TaskCategorizationPanel
              taskDescription={taskForm.task_description}
              keyResultFocus={taskForm.key_result_focus}
              activityId={taskForm.activity_id}
              onCategorize={(gamificationData) => {
                setTaskForm(prev => ({ ...prev, ...gamificationData }));
              }}
              initialValues={taskForm.category ? {
                category: taskForm.category,
                xp_value: taskForm.xp_value,
                ep_value: taskForm.ep_value,
                skill_rewards: taskForm.skill_rewards
              } : null}
            />

            {selectedOfficer !== user?.email && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-300">
                This will be created as a tentative task requiring the officer's acceptance.
              </div>
            )}

            <div className="flex justify-between gap-3 pt-4 border-t border-slate-800">
              <Button data-testid="issues-back-btn" variant="outline" onClick={() => setStep(1)} className="text-slate-600 border-slate-700 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleClose} className="text-slate-600 border-slate-700 hover:bg-slate-800" data-testid="issues-cancel-btn-1">Cancel</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={createTaskMutation.isPending}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {createTaskMutation.isPending ? 'Creating...' : 'Create Task'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}